import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getDojos,
  deleteDojo,
  updateOrderDojo,
  getGroupsByDojo,
  addDojo,
  updateDojo,
} from "../../../../../../../services/dojoServices";

import { uploadDojoPhoto } from "../../../../../../../services/dojoPhotoServices";

import {
  buildDojoPayload,
  calculateDojosStats,
  filterDojos,
  getNextDojoOrder,
  normalizeOrderDojos,
  sortDojosByOrder,
  validateDojoPayload,
  validateImageFile,
} from "../utils/dojosSettingsUtils";

export function useDojosSettings() {
  const [dojos, setDojos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    query: "",
  });

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editableDojo, setEditableDojo] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deletingId, setDeletingId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const fetchDojos = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getDojos();
      setDojos(sortDojosByOrder(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error("Erreur chargement dojos :", error);
      toast.error("Impossible de charger les dojos.");
      setDojos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDojos();
  }, [fetchDojos]);

  const filteredDojos = useMemo(() => {
    return filterDojos(dojos, filters);
  }, [dojos, filters]);

  const stats = useMemo(() => calculateDojosStats(dojos), [dojos]);

  const filteredStats = useMemo(
    () => calculateDojosStats(filteredDojos),
    [filteredDojos]
  );

  const nextOrder = useMemo(() => getNextDojoOrder(dojos), [dojos]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      query: "",
    });
  };

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditableDojo(null);
    setUploadProgress(0);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (dojo) => {
    setFormMode("edit");
    setEditableDojo(dojo || null);
    setUploadProgress(0);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setEditableDojo(null);
    setFormMode("create");
    setUploadProgress(0);
  };

  const handleSubmitDojo = async ({ formData, file }) => {
    const payload = buildDojoPayload(formData);
    const payloadError = validateDojoPayload(payload);
    const fileError = validateImageFile(file);

    if (payloadError) {
      toast.error(payloadError);
      return;
    }

    if (fileError) {
      toast.error(fileError);
      return;
    }

    try {
      setSubmittingForm(true);
      setUploadProgress(0);

      if (formMode === "create") {
        const created = await addDojo(payload);
        const createdId = created?.id || created?.dojo?.id || created?.data?.id;

        if (file && createdId) {
          await uploadDojoPhoto(createdId, file, {
            format: "webp",
            onUploadProgress: (event) => {
              if (!event.total) return;
              setUploadProgress(Math.round((event.loaded * 100) / event.total));
            },
          });
        }

        toast.success("Dojo créé avec succès.");
      } else {
        if (!editableDojo?.id) {
          toast.error("Dojo introuvable.");
          return;
        }

        await updateDojo({
          ...payload,
          id: editableDojo.id,
        });

        if (file) {
          await uploadDojoPhoto(editableDojo.id, file, {
            format: "webp",
            onUploadProgress: (event) => {
              if (!event.total) return;
              setUploadProgress(Math.round((event.loaded * 100) / event.total));
            },
          });
        }

        toast.success("Dojo mis à jour avec succès.");
      }

      await fetchDojos();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur enregistrement dojo :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de l’enregistrement du dojo."
      );
    } finally {
      setSubmittingForm(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteDojo = async (dojo) => {
    if (!dojo?.id) return;

    try {
      setDeletingId(dojo.id);

      const groupes = await getGroupsByDojo(dojo.id);

      if (Array.isArray(groupes) && groupes.length > 0) {
        const names = groupes.map((groupe) => groupe.nom).join(", ");

        toast.error(
          `Impossible de supprimer « ${dojo.dojo_nom} », des groupes y sont rattachés : ${names}.`
        );

        return;
      }

      if (
        !window.confirm(
          `Êtes-vous sûr de vouloir supprimer le dojo « ${
            dojo.dojo_nom || "sans nom"
          } » ?`
        )
      ) {
        return;
      }

      await deleteDojo(dojo.id);

      toast.success("Dojo supprimé avec succès.");
      await fetchDojos();
    } catch (error) {
      console.error("Erreur suppression dojo :", error);
      toast.error(
        "Erreur lors de la vérification des groupes ou de la suppression."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const persistOrder = async (orderedRows) => {
    await updateOrderDojo(orderedRows);
  };

  const applyReorder = async (rows) => {
    const orderedRows = normalizeOrderDojos(rows);

    try {
      setReordering(true);
      setDojos(orderedRows);

      await persistOrder(orderedRows);
      toast.success("Ordre des dojos mis à jour.");
      await fetchDojos();
    } catch (error) {
      console.error("Erreur ordre dojos :", error);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      await fetchDojos();
    } finally {
      setReordering(false);
    }
  };

  const moveDojo = async (dojoId, direction) => {
    if (reordering || loading) return;

    const rows = sortDojosByOrder(dojos);
    const index = rows.findIndex((dojo) => dojo.id === dojoId);

    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= rows.length) return;

    const nextRows = [...rows];
    const current = nextRows[index];

    nextRows[index] = nextRows[targetIndex];
    nextRows[targetIndex] = current;

    await applyReorder(nextRows);
  };

  return {
    dojos,
    filteredDojos,
    loading,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,
    nextOrder,

    formDrawerOpen,
    formMode,
    editableDojo,
    submittingForm,
    uploadProgress,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleSubmitDojo,

    deletingId,
    reordering,
    moveDojo,

    fetchDojos,
    handleDeleteDojo,
  };
}